import {
  applyDesignerCommand,
  DESIGN_ANSWER_VERSION,
  bentTextBox,
  boxExtent,
  boxOverhangs,
  designAreaPrice,
  designAreaProblems,
  fittedBox,
  newDesignLayerId,
  newTextLayer,
  resolveDesignAreaConfig,
  shapeDef,
  type DesignAreaAnswer,
  type DesignAreaConfig,
  type DesignArtworkLayer,
  type DesignBox,
  type DesignImageLayer,
  type DesignLayer,
  type DesignProblem,
  type DesignShapeLayer,
  type DesignTextLayer,
  type DesignQrLayer,
  designerVerticalText,
  designerPatternTiles,
  type ResolvedDesignAreaConfig,
  type ShapeKind,
} from '@pz/scene';

/**
 * Everything the designer knows, with no DOM anywhere near it.
 *
 * The rule this file exists to enforce: a shopper's design is changed only by
 * a reducer. Dragging, pinching, typing, tapping a swatch — each ends in one
 * action, and the canvas re-reads what came out. That is what makes undo a
 * two-line function instead of a graveyard of half-restored state, and it is
 * what lets the interesting parts (snapping, hit-testing, the render cache
 * key, the text measurement) be tested at all: the widget has no DOM in tests
 * and the monorepo carries no jsdom, so logic that lives in an event handler
 * is logic nobody will ever check again.
 *
 * Millimetres throughout, in the zone's own frame (centre from the zone's
 * top-left) — the same numbers that reach the print file.
 */

/** Snapshots kept for undo. Fifty gestures is far more than anyone retraces. */
export const UNDO_LIMIT = 50;

/** How close (mm) a dragged edge or centre has to come before it sticks. */
export const SNAP_THRESHOLD_MM = 2;

/** Arrow-key step, and the Shift step. */
export const NUDGE_MM = 0.5;
export const NUDGE_FAST_MM = 5;

export interface DesignerState {
  answer: DesignAreaAnswer;
  selectedId: string | null;
  past: DesignAreaAnswer[];
  future: DesignAreaAnswer[];
  /**
   * The coalescing tag of the last edit. Two consecutive edits with the same
   * tag are one undo step — a slider dragged across its whole range, or a word
   * typed letter by letter, is one thing the shopper did.
   */
  tag: string | null;
}

/**
 * A change to one layer. An intersection of partials rather than
 * `Partial<DesignLayer>`, which would be a union of partials and so refuse
 * `{ sizeMm, color }` written in one object.
 */
export type LayerPatch =
  Partial<Omit<DesignTextLayer, 'kind' | 'id'>> &
  Partial<Omit<DesignImageLayer, 'kind' | 'id'>> &
  Partial<Omit<DesignArtworkLayer, 'kind' | 'id'>> &
  Partial<Omit<DesignShapeLayer, 'kind' | 'id'>> &
  Partial<Omit<DesignQrLayer, 'kind' | 'id'>>;

export type DesignerAction =
  | { type: 'select'; id: string | null }
  | { type: 'set'; answer: DesignAreaAnswer | null; select?: string | null; history?: boolean; dropFuture?: boolean; forgetLast?: boolean; tag?: string; resetHistory?: boolean }
  | { type: 'editSession'; snapshot: DesignerState; cancel: boolean }
  | { type: 'add'; layer: DesignLayer; select?: boolean }
  | { type: 'update'; id: string; patch: LayerPatch; tag?: string; silent?: boolean }
  | { type: 'box'; id: string; box: DesignBox; tag?: string; silent?: boolean }
  | { type: 'nudge'; id: string; dx: number; dy: number; tag?: string }
  | { type: 'reorder'; id: string; to: number }
  | { type: 'raise'; id: string }
  | { type: 'lower'; id: string }
  | { type: 'duplicate'; id: string }
  | { type: 'remove'; id: string }
  | { type: 'undo' }
  | { type: 'redo' };

export function emptyAnswer(): DesignAreaAnswer {
  return { v: DESIGN_ANSWER_VERSION, layers: [] };
}

export function createDesignerState(value: DesignAreaAnswer | null): DesignerState {
  return {
    answer: value && Array.isArray(value.layers) ? { ...value, v: DESIGN_ANSWER_VERSION, layers: [...value.layers] } : emptyAnswer(),
    selectedId: null,
    past: [],
    future: [],
    tag: null,
  };
}

export function layerById(answer: DesignAreaAnswer, id: string | null): DesignLayer | null {
  if (!id) return null;
  return answer.layers.find((layer) => layer.id === id) ?? null;
}

export function layerIndex(answer: DesignAreaAnswer, id: string): number {
  return answer.layers.findIndex((layer) => layer.id === id);
}

export function canUndo(state: DesignerState): boolean {
  return state.past.length > 0;
}

export function canRedo(state: DesignerState): boolean {
  return state.future.length > 0;
}

/** Layers of one kind, for the per-kind caps (`image.maxCount`). */
export function countKind(answer: DesignAreaAnswer, kind: DesignLayer['kind']): number {
  return answer.layers.filter((layer) => layer.kind === kind).length;
}

export type AddRefusal = { ok: true } | { ok: false; reason: 'kind' | 'max-layers' | 'max-images'; limit?: number };

/**
 * May one more layer of this kind be added? Asked before the reducer so the
 * caller can say why not; the reducer itself never silently drops work.
 */
export function canAddLayer(
  state: DesignerState,
  cfg: ResolvedDesignAreaConfig,
  kind: DesignLayer['kind'],
  options: { emoji?: boolean } = {},
): AddRefusal {
  const allowed = kind === 'text' ? cfg.allow.text
    : kind === 'image' ? cfg.allow.image
      : kind === 'shape' ? cfg.allow.shape
        : kind === 'qr' ? cfg.allow.qr
        : options.emoji ? cfg.allow.emoji : cfg.allow.artwork;
  if (!allowed) return { ok: false, reason: 'kind' };
  if (state.answer.layers.length >= cfg.maxLayers) return { ok: false, reason: 'max-layers', limit: cfg.maxLayers };
  if (kind === 'image' && countKind(state.answer, 'image') >= cfg.image.maxCount) {
    return { ok: false, reason: 'max-images', limit: cfg.image.maxCount };
  }
  return { ok: true };
}

function withLayers(answer: DesignAreaAnswer, layers: DesignLayer[]): DesignAreaAnswer {
  return { ...answer, v: DESIGN_ANSWER_VERSION, layers };
}

/** The selection, dropped when the layer it pointed at is gone. */
function keepSelection(answer: DesignAreaAnswer, id: string | null): string | null {
  return id && answer.layers.some((layer) => layer.id === id) ? id : null;
}

function commit(
  state: DesignerState,
  next: DesignAreaAnswer,
  tag: string | undefined,
  select?: string | null,
  silent?: boolean,
): DesignerState {
  if (stableKey(next) === stableKey(state.answer)) {
    const selectedId = keepSelection(next, select === undefined ? state.selectedId : select);
    return selectedId === state.selectedId ? state : { ...state, selectedId };
  }
  // A silent change is the designer catching up with itself — a measured text
  // width, a box re-fitted to the ink it actually produced. The shopper did
  // not do it, so it must not cost them an undo step (nor throw away a redo).
  if (silent) {
    return {
      answer: next,
      selectedId: keepSelection(next, select === undefined ? state.selectedId : select),
      past: state.past,
      future: state.future,
      tag: state.tag,
    };
  }
  const coalesce = Boolean(tag) && tag === state.tag;
  const past = coalesce ? state.past : [...state.past, state.answer].slice(-UNDO_LIMIT);
  return {
    answer: next,
    selectedId: keepSelection(next, select === undefined ? state.selectedId : select),
    past,
    future: [],
    tag: tag ?? null,
  };
}

/**
 * Keeps a layer's centre inside the zone.
 *
 * A layer may hang over the edge — that is what `clip` mode is for, and what
 * the red outline warns about in `inside` mode — but a centre outside the zone
 * means a layer the shopper can no longer reach, and an answer the validator
 * rejects as "entirely outside". Cheaper to prevent than to explain.
 */
export function clampCentre(cfg: ResolvedDesignAreaConfig, box: DesignBox): DesignBox {
  const x = Math.min(cfg.sizeMm.w, Math.max(0, box.x));
  const y = Math.min(cfg.sizeMm.h, Math.max(0, box.y));
  return x === box.x && y === box.y ? box : { ...box, x, y };
}

export interface SnapGuide {
  axis: 'x' | 'y';
  /** Where the line is drawn, in zone mm. */
  at: number;
  kind: 'centre' | 'edge' | 'safe' | 'layer';
}

export interface SnapOptions {
  thresholdMm?: number;
  /** Sibling layers, for the smart guides that align one layer to another. */
  others?: readonly DesignLayer[];
  /** Off while resizing from a fixed corner, where only the moving edge snaps. */
  centre?: boolean;
  edges?: boolean;
}

interface Candidate {
  at: number;
  kind: SnapGuide['kind'];
  /** Which part of the dragged box is being matched. */
  from: 'centre' | 'min' | 'max';
}

/**
 * Sticks a dragged box to the zone's centre, its edges, the safe margin and
 * the other layers — FPD's smart guides, but in millimetres so the threshold
 * means the same thing on a phone and on a 27-inch screen.
 */
export function snapBox(
  cfg: ResolvedDesignAreaConfig,
  box: DesignBox,
  options: SnapOptions = {},
): { box: DesignBox; guides: SnapGuide[] } {
  const threshold = options.thresholdMm ?? SNAP_THRESHOLD_MM;
  const useCentre = options.centre !== false;
  const useEdges = options.edges !== false;
  const extent = boxExtent(box);
  const guides: SnapGuide[] = [];
  let out = box;

  const axes: Array<{
    axis: 'x' | 'y';
    centre: number;
    min: number;
    max: number;
    size: number;
    zone: number;
  }> = [
    { axis: 'x', centre: box.x, min: extent.left, max: extent.right, size: extent.right - extent.left, zone: cfg.sizeMm.w },
    { axis: 'y', centre: box.y, min: extent.top, max: extent.bottom, size: extent.bottom - extent.top, zone: cfg.sizeMm.h },
  ];

  for (const info of axes) {
    const candidates: Candidate[] = [];
    if (useCentre) candidates.push({ at: info.zone / 2, kind: 'centre', from: 'centre' });
    if (useEdges) {
      candidates.push({ at: 0, kind: 'edge', from: 'min' }, { at: info.zone, kind: 'edge', from: 'max' });
      if (cfg.safeMarginMm > 0) {
        candidates.push(
          { at: cfg.safeMarginMm, kind: 'safe', from: 'min' },
          { at: info.zone - cfg.safeMarginMm, kind: 'safe', from: 'max' },
        );
      }
    }
    // `others` is the caller's list of SIBLINGS — the dragged layer is never
    // in it, or it would snap to where it already is.
    for (const other of options.others ?? []) {
      const o = boxExtent(other.box);
      const oCentre = info.axis === 'x' ? other.box.x : other.box.y;
      const oMin = info.axis === 'x' ? o.left : o.top;
      const oMax = info.axis === 'x' ? o.right : o.bottom;
      if (useCentre) candidates.push({ at: oCentre, kind: 'layer', from: 'centre' });
      if (useEdges) {
        candidates.push({ at: oMin, kind: 'layer', from: 'min' }, { at: oMax, kind: 'layer', from: 'max' });
      }
    }

    let best: { delta: number; candidate: Candidate } | undefined;
    for (const candidate of candidates) {
      const ours = candidate.from === 'centre' ? info.centre : candidate.from === 'min' ? info.min : info.max;
      const delta = candidate.at - ours;
      if (Math.abs(delta) > threshold) continue;
      if (!best || Math.abs(delta) < Math.abs(best.delta) - 1e-9) best = { delta, candidate };
    }
    if (!best) continue;
    out = info.axis === 'x'
      ? { ...out, x: round(out.x + best.delta) }
      : { ...out, y: round(out.y + best.delta) };
    guides.push({ axis: info.axis, at: round(best.candidate.at), kind: best.candidate.kind });
  }

  return { box: out, guides };
}

/** Rotation snapped to 15° while Shift (or a two-finger twist) asks for it. */
export function snapAngle(degrees: number, snap: boolean, step = 15): number {
  const wrapped = ((degrees % 360) + 360) % 360;
  if (!snap) return round(wrapped, 1);
  return round((Math.round(wrapped / step) * step) % 360, 1);
}

/**
 * True when part of the box hangs over the zone's edge.
 *
 * Kept as a named export because callers pass a resolved config, but the rule
 * itself is the platform's `boxOverhangs` — the designer no longer carries its
 * own copy of the slack or of the rotated-corner arithmetic.
 */
export function overhangs(cfg: ResolvedDesignAreaConfig, box: DesignBox): boolean {
  return boxOverhangs(cfg.sizeMm, box);
}

/** True when the box crosses the inner safe margin. */
export function crossesSafeArea(cfg: ResolvedDesignAreaConfig, box: DesignBox): boolean {
  if (cfg.safeMarginMm <= 0) return false;
  const m = cfg.safeMarginMm;
  const e = boxExtent(box);
  return e.left < m - 0.01 || e.top < m - 0.01 || e.right > cfg.sizeMm.w - m + 0.01 || e.bottom > cfg.sizeMm.h - m + 0.01;
}

/** A zone-mm point inside a (possibly rotated) box, with a touch tolerance. */
export function boxContains(box: DesignBox, point: { x: number; y: number }, tolMm = 0): boolean {
  const rad = (-(box.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const dx = point.x - box.x, dy = point.y - box.y;
  const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
  return Math.abs(lx) <= box.w / 2 + tolMm && Math.abs(ly) <= box.h / 2 + tolMm;
}

/**
 * Which layer a tap lands on: the topmost one whose box contains the point.
 * Hidden layers are skipped; locked ones are not — a shopper who locked a
 * layer still has to be able to select it to unlock it.
 */
export function hitTest(
  answer: DesignAreaAnswer,
  point: { x: number; y: number },
  options: { tolMm?: number; skip?: ReadonlySet<string>; sizeMm?: {w:number;h:number} } = {},
): string | null {
  const tol = options.tolMm ?? 0;
  for (let i = answer.layers.length - 1; i >= 0; i -= 1) {
    const layer = answer.layers[i]!;
    if (options.skip?.has(layer.id) || layer.visible === false) continue;
    if (boxContains(layer.box, point, tol)) return layer.id;
    if(layer.pattern&&options.sizeMm){try{if(designerPatternTiles(layer,options.sizeMm).some(tile=>boxContains(tile.box,point,tol)))return layer.id;}catch{/* invalid patterns are explained by the validator */}}
  }
  return null;
}

// ── The reducer ──────────────────────────────────────────────────────────────

export function reduceDesigner(
  state: DesignerState,
  action: DesignerAction,
  cfg: ResolvedDesignAreaConfig,
): DesignerState {
  // Locked content can be selected and explicitly unlocked, but never edited.
  if ('id' in action && action.id && ['box','nudge','duplicate','remove','update'].includes(action.type)) {
    const layer = layerById(state.answer, action.id);
    if (layer?.locked && !(action.type === 'update' && Object.keys(action.patch).length === 1 && action.patch.locked === false)) return state;
  }
  // Canvas gestures and keyboard actions obey the same group contract as MCP.
  if ('id' in action && action.id && ['box','nudge','duplicate','remove','update'].includes(action.type)) {
    const layer = layerById(state.answer, action.id);
    const box = action.type === 'box' ? action.box : action.type === 'update' ? action.patch.box : undefined;
    const silent = (action.type === 'box' || action.type === 'update') && action.silent;
    if (layer?.groupId && !silent && (box || action.type === 'nudge' || action.type === 'duplicate' || action.type === 'remove')) {
      try {
        const command = box ? { op: 'place', ids: [layer.id], box }
          : action.type === 'nudge' ? { op: 'move', ids: [layer.id], dx: action.dx, dy: action.dy }
          : { op: action.type === 'remove' ? 'delete' : 'duplicate', ids: [layer.id] };
        const next = applyDesignerCommand(cfg, state.answer, command);
        return commit(state, next, 'tag' in action ? action.tag : undefined);
      } catch { return state; }
    }
  }
  switch (action.type) {
    case 'select':
      return state.selectedId === action.id
        ? state
        : { ...state, selectedId: keepSelection(state.answer, action.id), tag: null };

    case 'editSession': {
      const before = action.snapshot;
      if (action.cancel || JSON.stringify(before.answer) === JSON.stringify(state.answer)) return { ...before, tag: null };
      return { ...state, past: [...before.past, before.answer].slice(-UNDO_LIMIT), future: [], tag: null };
    }
    case 'set': {
      const next = action.answer && Array.isArray(action.answer.layers)
        ? withLayers(action.answer, [...action.answer.layers])
        : emptyAnswer();
      if (action.history === false) {
        return {
          answer: next,
          selectedId: keepSelection(next, action.select ?? null),
          // `forgetLast` drops the entry the cancelled edit pushed: after a
          // cancel the design equals that snapshot, so an undo to it would be
          // a step that changes nothing.
          past: action.resetHistory ? [] : action.forgetLast ? state.past.slice(0, -1) : state.past,
          // `dropFuture` is how a cancel puts the design back without leaving a
          // redo entry behind: the phone's "OTKAŽI" restores the snapshot it
          // took when the sheet opened, and offering to redo the edit the
          // shopper just abandoned would be nonsense.
          future: action.resetHistory || action.dropFuture ? [] : state.future,
          tag: null,
        };
      }
      return commit(state, next, action.tag, action.select ?? null);
    }

    case 'add': {
      const next = withLayers(state.answer, [...state.answer.layers, action.layer]);
      return commit(state, next, undefined, action.select === false ? state.selectedId : action.layer.id);
    }

    case 'update': {
      const at = layerIndex(state.answer, action.id);
      if (at < 0) return state;
      const layer = state.answer.layers[at]!;
      // The patch is an intersection of partials, so this merge cannot change
      // the layer's `kind`; the cast is the price of one object type for four
      // layer kinds.
      const merged = { ...layer, ...action.patch } as DesignLayer;
      if (action.patch.box) merged.box = clampCentre(cfg, roundBox(action.patch.box));
      const layers = [...state.answer.layers];
      layers[at] = merged;
      return commit(state, withLayers(state.answer, layers), action.tag, undefined, action.silent);
    }

    case 'box': {
      const at = layerIndex(state.answer, action.id);
      if (at < 0) return state;
      const layer = state.answer.layers[at]!;
      const layers = [...state.answer.layers];
      layers[at] = { ...layer, box: clampCentre(cfg, roundBox(action.box)) };
      return commit(state, withLayers(state.answer, layers), action.tag, undefined, action.silent);
    }

    case 'nudge': {
      const at = layerIndex(state.answer, action.id);
      if (at < 0) return state;
      const layer = state.answer.layers[at]!;
      if (layer.locked) return state;
      const layers = [...state.answer.layers];
      layers[at] = {
        ...layer,
        box: clampCentre(cfg, roundBox({ ...layer.box, x: layer.box.x + action.dx, y: layer.box.y + action.dy })),
      };
      return commit(state, withLayers(state.answer, layers), action.tag ?? `nudge:${action.id}`);
    }

    case 'reorder': {
      const from = layerIndex(state.answer, action.id);
      if (from < 0) return state;
      const to = Math.max(0, Math.min(state.answer.layers.length - 1, Math.round(action.to)));
      if (to === from) return state;
      try {
        const next=applyDesignerCommand(cfg,state.answer,{op:'reorder',ids:[action.id],index:to});
        return commit(state,next,undefined);
      } catch { return state; }
    }

    case 'raise':
    case 'lower': {
      const from = layerIndex(state.answer, action.id);
      if (from < 0) return state;
      const to = action.type === 'raise' ? from + 1 : from - 1;
      if (to < 0 || to >= state.answer.layers.length) return state;
      try {
        const next=applyDesignerCommand(cfg,state.answer,{op:'order',ids:[action.id],direction:action.type==='raise'?'forward':'backward'});
        return commit(state,next,undefined);
      } catch { return state; }
    }

    case 'duplicate': {
      const at = layerIndex(state.answer, action.id);
      if (at < 0) return state;
      const layer = state.answer.layers[at]!;
      if (state.answer.layers.length >= cfg.maxLayers) return state;
      const copy: DesignLayer = {
        ...layer,
        id: newDesignLayerId(),
        box: clampCentre(cfg, roundBox({ ...layer.box, x: layer.box.x + 2, y: layer.box.y + 2 })),
      };
      const layers = [...state.answer.layers];
      layers.splice(at + 1, 0, copy);
      return commit(state, withLayers(state.answer, layers), undefined, copy.id);
    }

    case 'remove': {
      const at = layerIndex(state.answer, action.id);
      if (at < 0) return state;
      const layers = state.answer.layers.filter((layer) => layer.id !== action.id);
      return commit(state, withLayers(state.answer, layers), undefined, null);
    }

    case 'undo': {
      if (!state.past.length) return state;
      const answer = state.past[state.past.length - 1]!;
      return {
        answer,
        selectedId: keepSelection(answer, state.selectedId),
        past: state.past.slice(0, -1),
        future: [state.answer, ...state.future].slice(0, UNDO_LIMIT),
        tag: null,
      };
    }

    case 'redo': {
      const answer = state.future[0];
      if (!answer) return state;
      return {
        answer,
        selectedId: keepSelection(answer, state.selectedId),
        past: [...state.past, state.answer].slice(-UNDO_LIMIT),
        future: state.future.slice(1),
        tag: null,
      };
    }

    default:
      return state;
  }
}

// ── Fresh layers ─────────────────────────────────────────────────────────────

/**
 * A small cascade so three artwork taps in a row do not land in one pile.
 * Five steps and back to the middle — beyond that the shopper has moved
 * something anyway.
 */
function cascade(cfg: ResolvedDesignAreaConfig, box: DesignBox, count: number): DesignBox {
  const step = Math.min(3, Math.max(1, Math.min(cfg.sizeMm.w, cfg.sizeMm.h) * 0.05));
  const at = count % 5;
  return clampCentre(cfg, { ...box, x: round(box.x + at * step), y: round(box.y + at * step) });
}

export function freshTextLayer(config: DesignAreaConfig, fontId: string, text?: string, count = 0): DesignTextLayer {
  const cfg = resolveDesignAreaConfig(config);
  const layer = newTextLayer(config, fontId, text);
  return { ...layer, box: cascade(cfg, layer.box, count) };
}

export function freshImageLayer(
  config: DesignAreaConfig,
  upload: { assetId: string; w: number; h: number },
  count = 0,
): DesignImageLayer {
  const cfg = resolveDesignAreaConfig(config);
  const aspect = upload.w > 0 && upload.h > 0 ? upload.w / upload.h : 1;
  return {
    id: newDesignLayerId(),
    kind: 'image',
    box: cascade(cfg, fittedBox(config, aspect, 0.6), count),
    assetId: upload.assetId,
    ...(upload.w > 0 && upload.h > 0 ? { sourcePx: { w: Math.round(upload.w), h: Math.round(upload.h) } } : {}),
  };
}

export function freshArtworkLayer(
  config: DesignAreaConfig,
  item: { assetId: string; libraryId?: string; vector?: boolean; w?: number; h?: number },
  count = 0,
): DesignArtworkLayer {
  const cfg = resolveDesignAreaConfig(config);
  const aspect = item.w && item.h && item.w > 0 && item.h > 0 ? item.w / item.h : 1;
  return {
    id: newDesignLayerId(),
    kind: 'artwork',
    box: cascade(cfg, fittedBox(config, aspect, 0.35), count),
    assetId: item.assetId,
    ...(item.libraryId ? { libraryId: item.libraryId } : {}),
    ...(item.vector ? { vector: true } : {}),
  };
}

export function freshShapeLayer(config: DesignAreaConfig, shape: ShapeKind, count = 0): DesignShapeLayer {
  const cfg = resolveDesignAreaConfig(config);
  const def = shapeDef(shape);
  return {
    id: newDesignLayerId(),
    kind: 'shape',
    box: cascade(cfg, fittedBox(config, def.aspect, 0.3), count),
    shape,
    fill: cfg.shape.palette[0] ?? { hex: '#000000' },
  };
}

// ── The render cache key ─────────────────────────────────────────────────────

/**
 * A stable string for any JSON-ish value: object keys sorted, so two layers
 * that differ only in the order their properties were assigned hash the same.
 * `JSON.stringify` alone does not have that property, and a cache keyed on it
 * re-rasterises the whole design the first time a panel writes `color` before
 * `size` instead of after.
 */
export function stableKey(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableKey).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableKey(v)}`).join(',')}}`;
}

/**
 * What a layer's raster depends on.
 *
 * Position and rotation are deliberately absent: moving a layer must never
 * cost a rasterisation, which is the whole reason the canvas is DOM nodes with
 * CSS transforms over Skia bitmaps rather than one big canvas. Opacity is
 * absent too — it is applied as CSS on the node. Size IS included: a resize
 * changes the pixels, so it re-renders once, on release.
 */
export function layerContentHash(layer: DesignLayer): string {
  const { box, opacity, locked, ...rest } = layer as DesignLayer & { opacity?: number; locked?: boolean };
  void opacity;
  void locked;
  return stableKey({ ...rest, w: round(box.w, 2), h: round(box.h, 2) });
}

/**
 * What a text layer's INK depends on, size of the box excluded. Used to decide
 * whether the box may be re-fitted to the ink: without it, fitting the box
 * changes the hash, which re-renders, which re-fits, forever.
 */
export function layerFitKey(layer: DesignLayer): string {
  if (layer.kind !== 'text') return layerContentHash(layer);
  // A fixed-width box is the one case where the box IS part of the ink: the
  // words wrap at its width, so dragging a side handle changes the line count
  // and therefore the height. Its width belongs in the key; its height still
  // does not, or fitting the height would re-fit for ever.
  const fixed = (layer as { boxMode?: string }).boxMode === 'fixed';
  return stableKey({
    text: layer.text,
    ...(fixed ? { wrapWidthMm: round(layer.box.w, 2), anchorV: (layer as { anchorV?: string }).anchorV ?? 'middle' } : {}),
    orientation: layer.orientation ?? 'horizontal',
    fontId: layer.fontId,
    sizeMm: round(layer.sizeMm, 3),
    tracking: layer.tracking ?? 0,
    uppercase: layer.uppercase ?? false,
    lineSpacing: layer.lineSpacing ?? 1.15,
    align: layer.align ?? 'center',
    outline: layer.outline ? { width: layer.outline.width } : null,
    shadow: layer.shadow ? { offset: layer.shadow.offset, blur: layer.shadow.blur, angle: layer.shadow.angle } : null,
    bend: layer.bend ?? 0,
    flipX: layer.flipX ?? false,
    flipY: layer.flipY ?? false,
  });
}

/**
 * How much room (mm) a layer's ink needs beyond its box: an outline grows
 * outwards from the glyph, a shadow is thrown clear of it, a stroke straddles
 * a shape's outline. Without this the raster clips exactly the decoration the
 * shopper just turned on.
 */
export function layerPadMm(layer: DesignLayer): number {
  if (layer.kind === 'text') {
    const outline = (layer.outline?.width ?? 0) * layer.sizeMm;
    const shadow = layer.shadow ? ((layer.shadow.offset + layer.shadow.blur) / 100) * layer.sizeMm : 0;
    return round(Math.min(layer.sizeMm * 2 + 2, 1.5 + outline + shadow), 2);
  }
  if (layer.kind === 'shape') return round(0.5 + (layer.stroke?.widthMm ?? 0) / 2 + 0.5, 2);
  return 0.5;
}

// ── Text measurement ─────────────────────────────────────────────────────────

/** The lines as the renderer will lay them out (uppercase applied). */
export function textLines(text: string, uppercase?: boolean): string[] {
  const value = uppercase ? text.toLocaleUpperCase('sr-RS') : text;
  return value.replace(/\r\n?/g, '\n').split('\n');
}

/**
 * The straight advance width of a text layer in mm, from a measurement of the
 * font at em size 1.
 *
 * `measureEm` is Skia (`renderer.measureText`) where the render bundle has
 * loaded, and an offscreen canvas 2D `measureText` where it has not; both
 * report a width for one line at a known font size, which the caller divides
 * by that size before handing it over. Tracking is added per character because
 * that is how the renderer advances: a 300/1000 em tracking on a ten-letter
 * word is three more em of width, and a box measured without it wraps.
 */
export function textAdvanceMm(
  lines: readonly string[],
  sizeMm: number,
  tracking: number | undefined,
  measureEm: (line: string) => number,
): number {
  const extra = ((tracking ?? 0) / 1000) * sizeMm;
  let widest = 0;
  for (const line of lines) {
    const glyphs = [...line].length;
    const width = measureEm(line) * sizeMm + extra * glyphs;
    if (width > widest) widest = width;
  }
  return round(Math.max(widest, sizeMm * 0.25), 3);
}

/**
 * The box a text layer needs: the measured advance width plus 4 % (the
 * renderer wraps a multiline text whose box is narrower than its widest line),
 * or the chord-and-sagitta box of the bent line.
 */
export function textBoxSize(layer: DesignTextLayer, widthMm: number): { w: number; h: number } {
  if (layer.bend) {
    const bent = bentTextBox(layer.sizeMm, widthMm, layer.bend);
    return { w: round(bent.w, 2), h: round(bent.h, 2) };
  }
  const lines = textLines(layer.orientation==='vertical'?designerVerticalText(layer.text):layer.text, layer.uppercase).length;
  const spacing = layer.lineSpacing ?? 1.15;
  const grow = (layer.outline?.width ?? 0) * layer.sizeMm * 2;
  return {
    w: round(Math.max(widthMm * 1.04, layer.sizeMm * 0.6) + grow, 2),
    h: round(layer.sizeMm * (lines > 1 ? spacing * lines : 1.2) + grow, 2),
  };
}

export interface InkBounds { left: number; top: number; right: number; bottom: number }

/**
 * The tightest rectangle of visible pixels in an RGBA buffer, or `undefined`
 * for an empty raster. This is how a text layer's handles come to hug the
 * letters instead of a generous guess: the renderer knows where the ink landed
 * and nothing else does.
 */
export function inkBounds(
  pixels: Uint8Array | Uint8ClampedArray,
  width: number,
  height: number,
  alphaThreshold = 8,
): InkBounds | undefined {
  if (width <= 0 || height <= 0 || pixels.length < width * height * 4) return undefined;
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y += 1) {
    const row = y * width * 4;
    for (let x = 0; x < width; x += 1) {
      if (pixels[row + x * 4 + 3]! < alphaThreshold) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      bottom = y;
    }
  }
  if (right < 0 || bottom < 0) return undefined;
  return { left, top, right, bottom };
}

/**
 * The box that hugs measured ink, keeping the ink exactly where it is on
 * screen. The raster covers the box plus `padMm` all round and is drawn
 * unrotated, so the ink's offset from the box centre has to be turned back
 * through the box's own rotation before it is applied.
 */
export function boxFromInk(box: DesignBox, ink: InkBounds, pxPerMm: number, padMm: number): DesignBox {
  if (pxPerMm <= 0) return box;
  const wPx = ink.right - ink.left + 1;
  const hPx = ink.bottom - ink.top + 1;
  const originX = -(box.w / 2 + padMm);
  const originY = -(box.h / 2 + padMm);
  const localCx = originX + (ink.left + wPx / 2) / pxPerMm;
  const localCy = originY + (ink.top + hPx / 2) / pxPerMm;
  const rad = ((box.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  return {
    ...box,
    x: round(box.x + localCx * cos - localCy * sin),
    y: round(box.y + localCx * sin + localCy * cos),
    w: round(Math.max(0.2, wPx / pxPerMm)),
    h: round(Math.max(0.2, hPx / pxPerMm)),
  };
}

/**
 * Whether the renderer lays this AUTO text box out at the box's own width.
 *
 * A single line is laid out unconstrained, so its box may hug the ink. A
 * paragraph — typed line breaks, or vertical text, which is one glyph per
 * line — is laid out AT `box.w`, and ink is narrower than the advance the
 * paragraph needs (side bearings, the outline's own inset). A box fitted to
 * the ink therefore wraps the widest line inside a word: "rođendan" printed
 * as "rođenda / n".
 */
export function autoTextLaysOutAtBoxWidth(layer: DesignTextLayer): boolean {
  return layer.orientation === 'vertical' || layer.text.includes('\n');
}

/**
 * `boxFromInk` for a paragraph: the height and the vertical position follow
 * the ink, the width and the horizontal position stay the measured layout's,
 * so the lines keep the column they were measured in.
 */
export function boxFromInkKeepingWidth(box: DesignBox, ink: InkBounds, pxPerMm: number, padMm: number): DesignBox {
  if (pxPerMm <= 0) return box;
  const hPx = ink.bottom - ink.top + 1;
  const originY = -(box.h / 2 + padMm);
  const localCy = originY + (ink.top + hPx / 2) / pxPerMm;
  const rad = ((box.rotation ?? 0) * Math.PI) / 180;
  return {
    ...box,
    x: round(box.x - localCy * Math.sin(rad)),
    y: round(box.y + localCy * Math.cos(rad)),
    h: round(Math.max(0.2, hPx / pxPerMm)),
  };
}

/** Whether a re-fit is worth a re-render: sub-tenth-of-a-millimetre is not. */
export function boxDiffers(a: DesignBox, b: DesignBox, toleranceMm = 0.15): boolean {
  return (
    Math.abs(a.x - b.x) > toleranceMm ||
    Math.abs(a.y - b.y) > toleranceMm ||
    Math.abs(a.w - b.w) > toleranceMm ||
    Math.abs(a.h - b.h) > toleranceMm
  );
}

// ── The store ────────────────────────────────────────────────────────────────

export interface DesignerMeta {
  price: number;
  problems: DesignProblem[];
}

export interface StoreTimers {
  setTimeout(handler: () => void, ms: number): number;
  clearTimeout(id: number): void;
}

export interface DesignerStoreOptions {
  config: DesignAreaConfig;
  value: DesignAreaAnswer | null;
  /** Every font the shop offers, for the `font-not-allowed` check. */
  fontIds?: ReadonlySet<string>;
  /**
   * The layers the zone came with. They are part of the product, not something
   * the shopper added, so the per-layer surcharges skip them — and the price
   * the designer reports has to agree with the one the card and the bill show.
   */
  authoredLayerIds?: readonly string[];
  /** How long after the last change `onChange` fires. Seam says ≤ 250 ms. */
  debounceMs?: number;
  onChange?(value: DesignAreaAnswer, meta: DesignerMeta): void;
  /**
   * The sticker contour the canvas has already traced for an answer, if any.
   * `designAreaProblems` turns it into `sticker-edge` / `sticker-islands`, so
   * a shopper is told the shape cannot be cut while they are still designing
   * rather than at the till. `undefined` = not traced yet, nothing reported.
   */
  stickerContour?(answer: DesignAreaAnswer): { touchesEdge: boolean; islands: number } | undefined;
  /** Injected in tests; `window` in the browser. */
  timers?: StoreTimers;
}

export interface DesignerStore {
  readonly config: ResolvedDesignAreaConfig;
  state(): DesignerState;
  value(): DesignAreaAnswer;
  meta(): DesignerMeta;
  dispatch(action: DesignerAction): DesignerState;
  subscribe(listener: (state: DesignerState, previous: DesignerState) => void): () => void;
  /** Reports now instead of after the debounce (pressing "Gotovo"). */
  flush(): void;
  destroy(): void;
}

const hostTimers: StoreTimers = {
  setTimeout: (handler, ms) => (typeof window === 'undefined'
    ? (setTimeout(handler, ms) as unknown as number)
    : window.setTimeout(handler, ms)),
  clearTimeout: (id) => {
    if (typeof window === 'undefined') clearTimeout(id as unknown as ReturnType<typeof setTimeout>);
    else window.clearTimeout(id);
  },
};

/**
 * The reducer, the history and the one debounced report to the host. The host
 * redraws the product preview from `onChange`, so it must not fire per
 * pointermove — but it must fire, eventually, for every change, including the
 * last one before the shopper taps Gotovo (hence `flush`).
 */
export function createDesignerStore(options: DesignerStoreOptions): DesignerStore {
  const cfg = resolveDesignAreaConfig(options.config);
  const timers = options.timers ?? hostTimers;
  const debounceMs = Math.max(0, options.debounceMs ?? 160);
  let state = createDesignerState(options.value);
  let pending: number | undefined;
  let dirty = false;
  let dead = false;
  const listeners = new Set<(state: DesignerState, previous: DesignerState) => void>();

  const metaOf = (answer: DesignAreaAnswer): DesignerMeta => ({
    price: designAreaPrice(options.config, answer, options.authoredLayerIds ? { authoredLayerIds: options.authoredLayerIds } : {}),
    // Assets and libraries are the server's to check: the browser knows no
    // ownership, and guessing would refuse a design the API would accept.
    problems: designAreaProblems(options.config, answer, {
      ...(options.fontIds ? { fontIds: options.fontIds } : {}),
      ...(options.stickerContour ? { stickerContour: options.stickerContour } : {}),
    }),
  });

  const report = (): void => {
    if (pending !== undefined) {
      timers.clearTimeout(pending);
      pending = undefined;
    }
    if (!dirty || dead) return;
    dirty = false;
    options.onChange?.(state.answer, metaOf(state.answer));
  };

  return {
    config: cfg,
    state: () => state,
    value: () => state.answer,
    meta: () => metaOf(state.answer),
    dispatch(action) {
      if (dead) return state;
      const previous = state;
      state = reduceDesigner(state, action, cfg);
      if (state === previous) return state;
      for (const listener of [...listeners]) {
        try {
          listener(state, previous);
        } catch (error) {
          console.error('[Designer] View update failed', error);
        }
      }
      if (state.answer !== previous.answer) {
        dirty = true;
        if (pending !== undefined) timers.clearTimeout(pending);
        pending = timers.setTimeout(report, debounceMs);
      }
      return state;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    flush: report,
    destroy() {
      dead = true;
      if (pending !== undefined) timers.clearTimeout(pending);
      pending = undefined;
      listeners.clear();
    },
  };
}

// ── Small shared maths ───────────────────────────────────────────────────────

export function round(value: number, places = 3): number {
  const f = 10 ** places;
  return Math.round(value * f) / f;
}

export function roundBox(box: DesignBox): DesignBox {
  return {
    x: round(box.x),
    y: round(box.y),
    w: round(Math.max(0.2, box.w)),
    h: round(Math.max(0.2, box.h)),
    ...(box.rotation ? { rotation: round(((box.rotation % 360) + 360) % 360, 2) } : {}),
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
